import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import { 
  FiArrowLeft, 
  FiPlus, 
  FiSearch, 
  FiLayers, 
  FiFileText, 
  FiTrash2, 
  FiBookOpen, 
  FiChevronLeft, 
  FiChevronRight,
  FiChevronDown,
  FiInbox,
  FiFilePlus,
  FiCloud,
  FiDownload,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { testService } from '../../../services/testService';
import toast from 'react-hot-toast';
import { useAssessmentContext } from '../AssessmentContext';
import '../dashboard/assessment.css';
import '../assessment-module-mobile.css';
import PedagogyDropdownMenu from './PedagogyDropdownMenu';
import PedagogyFieldHeader from './PedagogyFieldHeader';
import { AssessmentEditIcon, assessTipProps } from '../AssessmentContext';

const ASSESSMENT_ACTION_ICON_BTN_CLASS =
  'w-9 h-9 rounded-xl flex items-center justify-center border transition-all shadow-sm shrink-0';

const AssessmentEditIconButton = ({
  onClick,
  title = 'Edit',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 ${className}`}
  >
    <AssessmentEditIcon className="w-4 h-4 shrink-0" />
  </button>
);

const AssessmentDeleteIconButton = ({
  onClick,
  title = 'Delete',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-rose-100 bg-rose-50 text-rose-500 hover:bg-[#b91c1c] hover:text-white ${className}`}
  >
    <FiTrash2 className="w-4 h-4 shrink-0" strokeWidth={2} />
  </button>
);

const QB_GRID_MCQ = 'grid-cols-[60px_180px_minmax(0,1fr)_340px]';
const QB_GRID_DESC = 'grid-cols-[60px_180px_minmax(0,1fr)_72px]';

const MCQ_OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const isMcqQuestionType = (type) => String(type ?? '').toUpperCase() === 'MCQ';

const getMcqOptionText = (question, letter) => {
  const upper = String(letter ?? '').toUpperCase();
  const lower = upper.toLowerCase();
  if (!MCQ_OPTION_LETTERS.includes(upper)) return '';
  return String(question?.[`option_${lower}`] ?? question?.[`option_${upper}`] ?? '').trim();
};

const resolveMcqCorrectLetter = (question) => {
  const raw = String(question?.correct_answer ?? '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  if (MCQ_OPTION_LETTERS.includes(upper)) return upper;

  // Only "A", "A.", "A)" — not full answer text that happens to start with A
  const letterWithPunctuation = upper.match(/^([A-D])[\.\)\:\;\s-]*$/);
  if (letterWithPunctuation) return letterWithPunctuation[1];

  const rawLower = raw.toLowerCase();
  return (
    MCQ_OPTION_LETTERS.find((letter) => {
      const optionText = getMcqOptionText(question, letter).toLowerCase();
      return optionText !== '' && optionText === rawLower;
    }) || ''
  );
};

const isMcqCorrectOption = (question, letter) =>
  resolveMcqCorrectLetter(question) === String(letter ?? '').toUpperCase();

const normalizeMcqQuestionRow = (question) => {
  if (!isMcqQuestionType(question?.type)) return question;
  const resolved = resolveMcqCorrectLetter(question);
  return resolved ? { ...question, correct_answer: resolved } : question;
};

const DEFAULT_PEDAGOGY_OPTIONS = [
  'Algorithm Design',
  'Analytical Thinking',
  'Aptitude',
  'Code Efficiency',
  'Communication Skills',
  'Database Knowledge',
  'Data Interpretation',
  'Debugging',
  'Logical Reasoning',
];

const QB_CSV_HEADERS = [
  'section_name',
  'question',
  'type',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'correct_answer',
  'marks',
  'expected_answer',
  'pedagogy',
];

const QB_CSV_INSTRUCTION =
  '# type: MCQ or Descriptive question. MCQ: fill options and correct_answer (A-D). Descriptive: leave options empty; put the model answer in correct_answer (and expected_answer if used).';

const QB_TEMPLATE_COLUMNS_LABEL = QB_CSV_HEADERS.join(', ');

const normalizeQuestionBankHeaderKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const validateQuestionBankHeaderRow = (headerRow) => {
  const trimmed = headerRow.map((cell) => String(cell ?? '').trim());
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop();
  }

  if (trimmed.length !== QB_CSV_HEADERS.length) {
    return {
      valid: false,
      error: `Invalid column count (${trimmed.length}). Use the downloaded template with exactly these columns in order: ${QB_TEMPLATE_COLUMNS_LABEL}.`,
    };
  }

  const mismatches = [];
  trimmed.forEach((header, index) => {
    const expected = QB_CSV_HEADERS[index];
    if (normalizeQuestionBankHeaderKey(header) !== normalizeQuestionBankHeaderKey(expected)) {
      mismatches.push(`column ${index + 1} must be "${expected}" (found "${header}")`);
    }
  });

  if (mismatches.length > 0) {
    return {
      valid: false,
      error: `Invalid column names. ${mismatches.join('; ')}. Download the template and keep the header row unchanged.`,
    };
  }

  return { valid: true };
};

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const parseQuestionBankCsvLine = (line) => {
  const result = [];
  let curValue = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(curValue.trim());
      curValue = '';
    } else {
      curValue += char;
    }
  }
  result.push(curValue.trim());
  return result;
};

const normalizeQuestionBankCsvType = (typeRaw) => {
  const type = String(typeRaw || '').trim().toLowerCase();
  if (
    type === 'short answer' ||
    type === 'descriptive' ||
    type === 'descriptive question' ||
    type.includes('descriptive')
  ) {
    return 'DESCRIPTIVE';
  }
  return 'MCQ';
};

const rowToQuestionBankItem = (cols, rowIndex) => {
  const cells = QB_CSV_HEADERS.map((_, index) => String(cols[index] ?? '').trim());
  const question = cells[1];
  if (!question) return null;

  const type = normalizeQuestionBankCsvType(cells[2]);
  const expectedAnswer = cells[9];
  const correctAnswer = cells[7] || (type === 'DESCRIPTIVE' ? expectedAnswer : '');

  return {
    id: Date.now() + rowIndex,
    section_name: cells[0] || 'General',
    question,
    type,
    option_a: cells[3],
    option_b: cells[4],
    option_c: cells[5],
    option_d: cells[6],
    correct_answer: correctAnswer,
    marks: cells[8] || (type === 'DESCRIPTIVE' ? '2' : '1'),
    pedagogy: cells[10] || 'General',
  };
};

const parseQuestionBankTableRows = (tableRows) => {
  const dataRows = (tableRows || [])
    .map((row) => (Array.isArray(row) ? row : Object.values(row)).map((cell) => String(cell ?? '').trim()))
    .filter((row) => {
      const first = String(row[0] ?? '').trim();
      return row.some((cell) => cell !== '') && !first.startsWith('#');
    });

  if (dataRows.length === 0) {
    return { success: false, error: 'File is empty. Download the template and add your questions below the header row.' };
  }

  const headerCheck = validateQuestionBankHeaderRow(dataRows[0]);
  if (!headerCheck.valid) {
    return { success: false, error: headerCheck.error };
  }

  const questions = [];
  for (let i = 1; i < dataRows.length; i += 1) {
    const item = rowToQuestionBankItem(dataRows[i], i);
    if (item) questions.push(item);
  }

  if (questions.length === 0) {
    return {
      success: false,
      error: 'No valid question rows found. Each row needs a value in the "question" column.',
    };
  }

  return { success: true, questions };
};

const parseQuestionBankCsv = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const dataLines = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    dataLines.push(line);
  });

  const tableRows = dataLines.map((line) => parseQuestionBankCsvLine(line));
  return parseQuestionBankTableRows(tableRows);
};

const parseQuestionBankExcel = (arrayBuffer) => {
  try {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return { success: false, error: 'Excel file has no worksheets.' };
    }

    const sheet = workbook.Sheets[sheetName];
    const tableRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    return parseQuestionBankTableRows(tableRows);
  } catch (err) {
    console.error(err);
    return { success: false, error: 'Could not read the Excel file. Save as .csv from the template or upload a valid .xlsx file.' };
  }
};

const QuestionBankManager = ({ 
  onBack, 
  returnToTemplates = false,
  isWizardMode = false,
  selectedQBId = null,
  onSelectQB = null,
  draftQuestionBank = null
}) => {
  const { returnFromModule } = useAssessmentContext();
  const [activeTab, setActiveTab] = useState('MCQ');
  const [searchTerm, setSearchTerm] = useState('');
  const [bankName, setBankName] = useState('');
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [pedagogySearch, setPedagogySearch] = useState('');
  const [pedagogyOptions, setPedagogyOptions] = useState(DEFAULT_PEDAGOGY_OPTIONS);
  const [savingPedagogy, setSavingPedagogy] = useState(false);
  const [pedagogyDeleteMode, setPedagogyDeleteMode] = useState(false);
  const [selectedPedagogiesForDelete, setSelectedPedagogiesForDelete] = useState([]);
  const [deletingPedagogies, setDeletingPedagogies] = useState(false);
  const [showPedagogyDropdown, setShowPedagogyDropdown] = useState(false);
  const [correctOption, setCorrectOption] = useState(null);
  const [banks, setBanks] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [newOptions, setNewOptions] = useState({ A: '', B: '', C: '', D: '' });
  const [newMarks, setNewMarks] = useState(1);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingBankId, setEditingBankId] = useState(null);
  const [editBankName, setEditBankName] = useState('');
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [popupInput, setPopupInput] = useState('');
  const [viewingQuestion, setViewingQuestion] = useState(null);

  const lastCalledQB = useRef(null);

  // Initialize from draftQuestionBank if we are in wizard mode and resuming a draft
  useEffect(() => {
    if (isWizardMode && draftQuestionBank) {
      if (draftQuestionBank.id) {
        setSelectedBankId(draftQuestionBank.id);
      } else {
        setSelectedBankId(null);
      }
      if (draftQuestionBank.name) {
        setBankName(draftQuestionBank.name);
      }
      if (draftQuestionBank.questions) {
        setQuestions(draftQuestionBank.questions);
      }
    }
  }, [draftQuestionBank, isWizardMode]);

  useEffect(() => {
    if (isWizardMode) {
      if (selectedQBId) {
        if (String(selectedQBId) !== String(selectedBankId)) {
          setSelectedBankId(selectedQBId);
          const bank = banks.find(b => String(b.id) === String(selectedQBId));
          if (bank) {
            setBankName(bank.name);
            const mapped = (bank.rawQuestions || []).map((q) =>
              normalizeMcqQuestionRow({
                id: q.id,
                section_name: q.category,
                question: q.question,
                type: q.type,
                option_a: q.option_a,
                option_b: q.option_b,
                option_c: q.option_c,
                option_d: q.option_d,
                correct_answer: q.correct_answer,
                marks: q.marks,
                pedagogy: q.pedagogy,
                status: q.status || 'Active',
              })
            );
            setQuestions(mapped);
          }
        }
      } else {
        if (selectedBankId !== null) {
          setSelectedBankId(null);
          setBankName('');
          setQuestions([]);
          setIsAddingQuestion(false);
        }
      }
    }
  }, [selectedQBId, banks, isWizardMode]);

  useEffect(() => {
    if (isWizardMode && onSelectQB) {
      if (selectedBankId || bankName.trim()) {
        const qbPayload = {
          id: selectedBankId,
          name: bankName,
          questions: questions
        };
        
        const isIdentical = lastCalledQB.current &&
          lastCalledQB.current.id === qbPayload.id &&
          lastCalledQB.current.name === qbPayload.name &&
          lastCalledQB.current.questions.length === qbPayload.questions.length &&
          lastCalledQB.current.questions.every((q, idx) => q.id === qbPayload.questions[idx]?.id);
          
        if (!isIdentical) {
          lastCalledQB.current = qbPayload;
          onSelectQB(qbPayload);
        }
      } else {
        if (lastCalledQB.current !== null) {
          lastCalledQB.current = null;
          onSelectQB(null);
        }
      }
    }
  }, [selectedBankId, bankName, questions, isWizardMode, onSelectQB]);

  
  const fetchBanks = async () => {
    setLoading(true);
    try {
      const res = await testService.getQuestionBanks();
      if (res.status === 'success') {
        setBanks(res.banks.map(b => ({
          id: b.id,
          name: b.name,
          questions: b.question_count,
          sections: b.section_count,
          rawQuestions: (b.questions || []).map(q => ({ ...q, status: q.status || 'Active' }))
        })));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const handleBulkUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = isExcel
        ? parseQuestionBankExcel(e.target.result)
        : parseQuestionBankCsv(e.target.result);

      if (!result.success) {
        toast.error(result.error);
        return;
      }

      setQuestions((prev) => [...prev, ...result.questions]);
      toast.success(`${result.questions.length} questions imported to draft.`);
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }

    event.target.value = null;
  };

  const pedagogyRef = useRef(null);
  const pedagogyAnchorRef = useRef(null);
  const pedagogyMenuRef = useRef(null);
  const [pedagogyDropdownPos, setPedagogyDropdownPos] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadPedagogies = async () => {
      try {
        const res = await testService.getPedagogies();
        if (!cancelled && res?.status === 'success' && Array.isArray(res.pedagogies) && res.pedagogies.length > 0) {
          setPedagogyOptions(res.pedagogies);
        }
      } catch (err) {
        console.error('Failed to load pedagogies:', err);
      }
    };
    loadPedagogies();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedPedagogySearch = pedagogySearch.trim();
  const filteredPedagogyOptions = pedagogyOptions.filter((opt) =>
    opt.toLowerCase().includes(trimmedPedagogySearch.toLowerCase())
  );
  const canSaveNewPedagogy =
    trimmedPedagogySearch.length > 0 &&
    !pedagogyOptions.some((opt) => opt.toLowerCase() === trimmedPedagogySearch.toLowerCase());

  const handleSelectPedagogy = useCallback((value) => {
    setPedagogySearch(value);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  }, []);

  const handleSaveNewPedagogy = useCallback(async () => {
    if (!canSaveNewPedagogy || savingPedagogy) return;
    setSavingPedagogy(true);
    try {
      const res = await testService.savePedagogy(trimmedPedagogySearch);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        } else if (res.pedagogy) {
          setPedagogyOptions((prev) => {
            const next = [...prev, res.pedagogy];
            return next.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          });
        }
        setPedagogySearch(res.pedagogy || trimmedPedagogySearch);
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        toast.success(res.created === false ? 'Pedagogy already exists' : 'Pedagogy saved');
      } else {
        toast.error(res?.message || 'Failed to save pedagogy');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save pedagogy');
    } finally {
      setSavingPedagogy(false);
    }
  }, [canSaveNewPedagogy, savingPedagogy, trimmedPedagogySearch]);

  const syncPedagogyDropdownPosition = useCallback(() => {
    const anchor = pedagogyAnchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuMaxHeight = 240;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuMaxHeight + gap && rect.top > menuMaxHeight + gap;
    setPedagogyDropdownPos({
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
    });
  }, []);

  const exitPedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode(false);
    setSelectedPedagogiesForDelete([]);
  }, []);

  const togglePedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedPedagogiesForDelete([]);
        setShowPedagogyDropdown(true);
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setSelectedPedagogiesForDelete([]);
      }
      return next;
    });
  }, [syncPedagogyDropdownPosition]);

  const togglePedagogyDeleteSelect = useCallback((name) => {
    setSelectedPedagogiesForDelete((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  }, []);

  const handleConfirmDeletePedagogies = useCallback(async () => {
    if (selectedPedagogiesForDelete.length === 0 || deletingPedagogies) return;
    setDeletingPedagogies(true);
    try {
      const res = await testService.deletePedagogies(selectedPedagogiesForDelete);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        }
        const deletedLower = new Set(
          (res.deleted || selectedPedagogiesForDelete).map((n) => String(n).toLowerCase())
        );
        if (deletedLower.has((pedagogySearch || '').toLowerCase())) {
          setPedagogySearch('');
        }
        exitPedagogyDeleteMode();
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        const count = res.deleted?.length ?? selectedPedagogiesForDelete.length;
        toast.success(count === 1 ? 'Pedagogy removed' : `${count} pedagogies removed`);
      } else {
        toast.error(res?.message || 'Failed to delete pedagogies');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete pedagogies');
    } finally {
      setDeletingPedagogies(false);
    }
  }, [
    selectedPedagogiesForDelete,
    deletingPedagogies,
    pedagogySearch,
    exitPedagogyDeleteMode,
  ]);

  const pedagogyDropdownMenuProps = {
    deleteMode: pedagogyDeleteMode,
    selectedForDelete: selectedPedagogiesForDelete,
    onToggleDeleteSelect: togglePedagogyDeleteSelect,
    onConfirmDelete: handleConfirmDeletePedagogies,
    deletingSelected: deletingPedagogies,
    onCancelDeleteMode: exitPedagogyDeleteMode,
    canSaveNew: pedagogyDeleteMode ? false : canSaveNewPedagogy,
    savingNew: savingPedagogy,
    onSelect: handleSelectPedagogy,
    onSaveNew: handleSaveNewPedagogy,
  };

  const togglePedagogyDropdown = () => {
    setShowPedagogyDropdown((open) => {
      const next = !open;
      if (next) {
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setPedagogyDropdownPos(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!showPedagogyDropdown) return undefined;
    syncPedagogyDropdownPosition();
    const onScrollOrResize = () => syncPedagogyDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showPedagogyDropdown, syncPedagogyDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inField = pedagogyRef.current?.contains(event.target);
      const inMenu = pedagogyMenuRef.current?.contains(event.target);
      if (!inField && !inMenu) {
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        setPedagogyDeleteMode(false);
        setSelectedPedagogiesForDelete([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddQuestion = () => {
    setEditingQuestionId(null);
    setIsAddingQuestion(true);
    setPedagogySearch('');
    setCorrectOption(null);
    setNewQuestion('');
    setNewOptions({ A: '', B: '', C: '', D: '' });
    setNewCorrectAnswer('');
    setNewMarks(1);
  };

  const handleCancelInlineEdit = () => {
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  };

  const selectBank = (bank) => {
    setBankName(bank.name);
    setSelectedBankId(bank.id);
    const mapped = bank.rawQuestions.map((q) =>
      normalizeMcqQuestionRow({
        id: q.id,
        section_name: q.category,
        question: q.question,
        type: q.type,
        option_a: q.option_a,
        option_b: q.option_b,
        option_c: q.option_c,
        option_d: q.option_d,
        correct_answer: q.correct_answer,
        marks: q.marks,
        pedagogy: q.pedagogy,
        status: q.status || 'Active',
      })
    );
    setQuestions(mapped);
  };

  const handleToggleStatus = async (q) => {
    const newStatus = (q.status || 'Active') === 'Active' ? 'Inactive' : 'Active';
    // Optimistically update UI
    setQuestions((prev) => prev.map((item) => item.id === q.id ? { ...item, status: newStatus } : item));
    // If question has a real DB id (not a local timestamp), persist
    const isRealDbId = typeof q.id === 'number' && q.id < 1000000000000;
    if (q.id && isRealDbId) {
      try {
        await testService.updateQBQuestionStatus(q.id, newStatus);
        toast.success(newStatus === 'Active' ? 'Question activated.' : 'Question deactivated.');
      } catch (err) {
        console.error(err);
        // Rollback
        setQuestions((prev) => prev.map((item) => item.id === q.id ? { ...item, status: q.status || 'Active' } : item));
        toast.error('Failed to update question status.');
      }
    } else {
      toast.success(newStatus === 'Active' ? 'Question activated.' : 'Question deactivated.');
    }
  };

  const handleEditQuestion = (q) => {
    setIsAddingQuestion(false);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
    setActiveTab(q.type === 'MCQ' ? 'MCQ' : 'DESCRIPTIVE');
    setEditingQuestionId(q.id);
    setNewQuestion(q.question.replace(/\s*\(Q\d+\)$/i, ''));
    setPedagogySearch(q.pedagogy || '');
    setNewMarks(q.marks);
    if (isMcqQuestionType(q.type)) {
      setNewOptions({ A: q.option_a, B: q.option_b, C: q.option_c, D: q.option_d });
      setCorrectOption(resolveMcqCorrectLetter(q) || q.correct_answer);
    } else {
      setNewCorrectAnswer(q.correct_answer || '');
    }
  };

  const handleDeleteQuestion = (id) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this question?',
      message: 'Are you sure you want to delete this question? This will remove it from the draft.',
      confirmText: 'Yes, delete it!',
      onConfirm: () => {
        setQuestions(prev => prev.filter(q => q.id !== id));
        if (editingQuestionId === id) handleCancelInlineEdit();
        setPopupConfig({ isOpen: false });
        toast.success('Question deleted successfully');
      }
    });
  };

  const handleManualSave = () => {
    if (!newQuestion.trim()) return toast.error("Question text is required");
    if (activeTab === 'MCQ') {
      if (!newOptions.A || !newOptions.B || !newOptions.C || !newOptions.D) return toast.error("All options are required for MCQ");
      if (!correctOption) return toast.error("Please select a correct option");
    }

    const existingQuestion = editingQuestionId ? questions.find(q => q.id === editingQuestionId) : null;

    const questionObj = normalizeMcqQuestionRow({
      id: editingQuestionId || Date.now(),
      section_name: 'General',
      question: newQuestion,
      type: activeTab === 'MCQ' ? 'MCQ' : 'DESCRIPTIVE',
      option_a: newOptions.A,
      option_b: newOptions.B,
      option_c: newOptions.C,
      option_d: newOptions.D,
      correct_answer: activeTab === 'MCQ' ? correctOption : newCorrectAnswer,
      marks: newMarks,
      pedagogy: pedagogySearch || 'General',
      status: existingQuestion?.status || 'Active',
    });

    if (editingQuestionId) {
      setQuestions((prev) => prev.map((q) => (q.id === editingQuestionId ? questionObj : q)));
    } else {
      setQuestions((prev) => [...prev, questionObj]);
    }

    const wasEditing = !!editingQuestionId;
    setIsAddingQuestion(false);
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
    toast.success(wasEditing ? 'Question updated.' : 'Question added to draft.');
  };

  const handleRenameBank = (bank) => {
    setPopupInput(bank.name);
    setPopupConfig({
      isOpen: true,
      type: 'info',
      title: 'Rename Question Bank',
      message: 'Enter the new name for this question bank.',
      showInput: true,
      inputPlaceholder: 'Bank Name...',
      confirmText: 'Rename',
      onConfirm: async () => {
        if (!popupInput.trim()) return toast.error("Name cannot be empty");
        try {
          await testService.renameQuestionBank(bank.id, popupInput);
          fetchBanks();
          setPopupConfig({
            isOpen: true,
            type: 'success',
            title: 'Renamed!',
            message: 'Question bank has been renamed successfully.',
            showCancel: false,
            confirmText: 'OK',
            onConfirm: () => setPopupConfig({ isOpen: false })
          });
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
        }
      }
    });
  };

  const handleDownloadTemplate = () => {
    const sampleRows = [
      ['General', 'What is 2+2?', 'MCQ', '3', '4', '5', '6', 'B', '1', '', 'Factual'],
      [
        'General',
        'Explain gravity.',
        'Descriptive question',
        '',
        '',
        '',
        '',
        'Force that pulls objects towards each other.',
        '2',
        'Force that pulls objects towards each other.',
        'Conceptual',
      ],
    ];
    const csvContent = [
      QB_CSV_INSTRUCTION,
      QB_CSV_HEADERS.map(escapeCsvCell).join(','),
      ...sampleRows.map((row) => row.map(escapeCsvCell).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'question_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSave = async () => {
    if (!bankName.trim()) {
      toast.error("Please provide a name for the Question Bank.");
      return;
    }
    if (questions.length === 0) {
      toast.error("Please add at least one question to the bank.");
      return;
    }

    setLoading(true);
    try {
      let repoId = selectedBankId;
      if (!repoId) {
        const bankRes = await testService.saveQuestionBank({ name: bankName });
        if (bankRes.status === 'success') {
          repoId = bankRes.id;
        } else {
          throw new Error("Failed to create bank");
        }
      }

      if (repoId) {
        const bulkRes = await testService.bulkSaveQBQuestions({
          repository_id: repoId,
          questions: questions
        });
        if (bulkRes.status === 'success') {
          toast.success('Question Bank updated successfully.');
          setQuestions([]);
          setBankName('');
          setSelectedBankId(null);
          fetchBanks();
          if (returnToTemplates) {
            returnFromModule();
          }
        } else {
          toast.error(bulkRes.message || "Failed to save bank.");
        }
      }
    } catch (err) {
      toast.error("Failed to save bank.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBank = (id, e) => {
    e.stopPropagation();
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this bank?',
      message: 'Are you sure you want to delete this bank? All associated questions will be affected.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          const res = await testService.deleteQuestionBank(id);
          setPopupConfig({ isOpen: false });
          if (res?.status === 'success') {
            if (String(selectedBankId) === String(id)) {
              setSelectedBankId(null);
              setBankName('');
              setQuestions([]);
            }
            fetchBanks();
            toast.success('Question bank deleted successfully');
          } else {
            toast.error(res?.message || 'Failed to delete question bank');
          }
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          toast.error('Failed to delete question bank');
        }
      }
    });
  };

  const filteredBanks = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return banks;
    return banks.filter((bank) => String(bank.name || '').toLowerCase().includes(query));
  }, [banks, searchTerm]);

  return (
    <div className={isWizardMode ? "flex flex-col flex-1 min-h-0 bg-[#f8fafc] overflow-hidden font-sans rounded-3xl border border-slate-100 shadow-xl" : "assess-mod-page assess-mod-qb-page flex flex-col h-full min-h-0 bg-[#f8fafc] overflow-hidden font-sans"}>
      {/* Top Header */}
      {!isWizardMode && (
        <header className="assess-mod-header h-[70px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="assess-mod-back-mobile lg:hidden"
            aria-label="Go back"
          >
            <FiArrowLeft className="text-lg" />
          </button>
          <div className="assess-mod-header-brand flex items-center gap-4">
            <div className="w-10 h-10 bg-[#b91c1c] rounded-xl flex items-center justify-center shadow-lg shadow-red-100">
              <FiBookOpen className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-[16px] font-black text-[#1e293b] tracking-tight leading-none mb-0">
                Question bank
              </h1>
            </div>
          </div>
          <div className="assess-mod-header-actions flex items-center gap-3">
            {selectedBankId && (
              <button 
                onClick={() => {
                  setBankName('');
                  setSelectedBankId(null);
                  setQuestions([]);
                  setIsAddingQuestion(false);
                }}
                className="btn-assessment btn-assessment-danger"
              >
                <FiPlus className="text-sm" /> Create QB
              </button>
            )}
            {!isWizardMode && (
              <button
                type="button"
                onClick={onBack}
                className="btn-assessment btn-assessment-danger hidden lg:inline-flex"
              >
                <FiArrowLeft className="text-sm" /> Back
              </button>
            )}
          </div>
        </header>
      )}

      <div className="assess-mod-body flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <aside className="assess-mod-sidebar w-full lg:w-[300px] bg-[#f8fafc] border-e border-slate-200 flex flex-col shrink-0 overflow-hidden">
          <div className="assess-mod-sidebar-search bg-white p-6 border-b border-slate-200 shrink-0">
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input 
                type="text"
                placeholder="Search banks..."
                className="w-full bg-[#f1f5f9] border border-slate-200 rounded-xl pl-10 pr-4 py-2 text-[12px] font-bold focus:ring-4 focus:ring-red-50 transition-all placeholder:text-slate-400 shadow-inner"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="assess-mod-sidebar-list flex-1 min-h-0 p-6 overflow-y-auto custom-scrollbar">
            <div className="space-y-3">
              {filteredBanks.length === 0 ? (
                <p className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest py-6 px-2">
                  {banks.length === 0 ? 'No question banks yet' : 'No banks match your search'}
                </p>
              ) : (
                filteredBanks.map((bank) => (
                  <div
                    key={bank.id}
                    onClick={() => selectBank(bank)}
                    className={`relative overflow-hidden p-3 rounded-[24px] border border-red-100 transition-all cursor-pointer group flex items-center gap-3 ${selectedBankId === bank.id ? 'bg-red-50 shadow-sm' : 'bg-white hover:border-red-200'}`}
                  >
                    <div className="w-10 h-10 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-[#b91c1c] shadow-sm shrink-0">
                      <FiFileText className="text-lg" />
                    </div>
                    <div className="flex-1 min-w-0 pr-2">
                      <h5 className={`text-[13px] font-black mb-0 tracking-tight truncate ${selectedBankId === bank.id ? 'text-[#b91c1c]' : 'text-[#1e293b]'}`}>{bank.name}</h5>
                      <p className="text-[8px] text-slate-400 font-black uppercase tracking-widest mt-0.5 truncate">
                        {bank.questions} QUESTIONS • {bank.sections} SECTIONS
                      </p>
                    </div>
                    <div 
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all bg-white/95 backdrop-blur-sm pl-4 py-2 rounded-l-xl z-10" 
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => handleDeleteBank(bank.id, e)}
                        title="Delete question bank"
                        className="w-8 h-8 bg-red-50 text-red-500 rounded-lg flex items-center justify-center border border-red-100 hover:bg-[#b91c1c] hover:text-white transition-all shadow-sm"
                      >
                        <FiTrash2 className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="assess-mod-main-wrap assess-mod-qb-main flex-1 min-w-0 min-h-0 overflow-hidden bg-[#f8fafc] flex flex-col">
          <div className="assess-mod-card-scroll assess-mod-qb-card-scroll flex-1 min-h-0 flex flex-col w-full max-w-[1600px] mx-auto">
          <div className="assess-mod-card assess-mod-qb-card flex-1 min-h-0 w-full bg-white border border-slate-100 shadow-2xl shadow-slate-200/40 flex flex-col overflow-hidden">
            
            {/* Repository Setup Header */}
            <div className="assess-mod-setup-row assess-mod-qb-setup-row flex items-center shrink-0">
              <div className="w-12 h-12 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-[#b91c1c] shadow-sm shrink-0">
                <FiFileText className="text-xl" />
              </div>
              
              <div className="flex-1 max-w-[260px]">
                <div className="mb-1">
                  <h6 className="text-[11px] font-black text-[#1e293b] uppercase tracking-widest mb-0">QUESTION BANK NAME</h6>
                </div>
                <input 
                  type="text"
                  placeholder="Enter Question Bank Name..."
                  readOnly={!!selectedBankId}
                  className={`w-full border border-slate-100 rounded-xl px-4 py-2 text-[12px] font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 shadow-inner ${
                    selectedBankId
                      ? 'bg-slate-50 cursor-default focus:ring-0'
                      : 'bg-[#f8fafc] focus:ring-2 focus:ring-red-100'
                  }`}
                  value={bankName}
                  onChange={(e) => {
                    if (!selectedBankId) setBankName(e.target.value);
                  }}
                />
              </div>

              <div className="assess-mod-stats-pill flex items-center bg-white rounded-[24px] border border-slate-100 overflow-hidden shadow-sm">
                <div className="px-6 py-2.5 text-center border-e border-slate-50">
                  <span className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">SECTIONS</span>
                  <span className="text-xl font-black text-[#1e293b] leading-none">
                    {(() => {
                      const activeQs = questions.filter(q => (q.status || 'Active') === 'Active');
                      const types = new Set(activeQs.map(q => {
                        const t = String(q.type || 'MCQ').toUpperCase();
                        return (t === 'DESCRIPTIVE' || t.includes('DESCRIPTIVE') || t === 'SHORT ANSWER') ? 'DESCRIPTIVE' : 'MCQ';
                      }));
                      return types.size;
                    })()}
                  </span>
                </div>
                <div className="px-6 py-2.5 text-center">
                  <span className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">QUESTIONS</span>
                  <span className="text-xl font-black text-[#b91c1c] leading-none">
                    {questions.filter(q => (q.status || 'Active') === 'Active').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs and Actions */}
            <div className="assess-mod-tabs-row assess-mod-qb-tabs-row flex items-center justify-between shrink-0">
              <div className="assess-mod-tab-list flex items-center">
                <button 
                  onClick={() => { setActiveTab('MCQ'); setIsAddingQuestion(false); setEditingQuestionId(null); }}
                  className={`text-[10px] font-black uppercase tracking-[0.2em] pb-3 relative transition-all ${activeTab === 'MCQ' ? 'text-[#b91c1c]' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  MCQ
                  {activeTab === 'MCQ' && <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#b91c1c] rounded-full"></div>}
                </button>
                <button 
                  onClick={() => { setActiveTab('DESCRIPTIVE'); setIsAddingQuestion(false); setEditingQuestionId(null); }}
                  className={`text-[10px] font-black uppercase tracking-[0.2em] pb-3 relative transition-all ${activeTab === 'DESCRIPTIVE' ? 'text-[#b91c1c]' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  DESCRIPTIVE QUESTION
                  {activeTab === 'DESCRIPTIVE' && <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#b91c1c] rounded-full"></div>}
                </button>
              </div>

              <div className="assess-mod-actions-row flex items-center gap-3">
                <button 
                  onClick={handleDownloadTemplate}
                  className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6"
                >
                  <FiDownload className="text-sm" /> Template
                </button>
                <button 
                  onClick={() => fileInputRef.current.click()}
                  className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6"
                >
                  <FiCloud className="text-sm" /> Bulk Upload
                </button>
                <input 
                  type="file"
                  ref={fileInputRef}
                  onChange={handleBulkUpload}
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  className="hidden"
                />
                <button 
                  onClick={handleAddQuestion}
                  className="btn-assessment btn-assessment-danger !py-2.5 !px-6"
                >
                  <FiPlus className="text-sm" /> Add Question
                </button>
              </div>
            </div>

            {/* Table Area — fills card; scrolls inside question list */}
            <div className="assess-mod-table-panel flex-1 min-h-0 flex flex-col w-full min-w-0">
              <div className="assess-mod-table-panel-inner flex-1 min-h-0 flex flex-col bg-[#f8fafc]/50 rounded-[32px] border border-slate-100 w-full min-w-0 overflow-hidden">
                <div className="assess-mod-question-area assess-mod-qb-question-area flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pt-4 pb-0 w-full min-w-0 custom-scrollbar">
                  <div className={`assess-mod-question-scroll flex flex-col w-full min-w-0 min-h-full ${isWizardMode ? 'pb-20' : ''}`}>
                  {/* Column headers */}
                  <div className={`pb-4 ${isAddingQuestion ? 'max-lg:hidden' : ''}`}>
                    <div className={`assess-mod-grid-header grid gap-6 px-6 py-2.5 bg-white border border-slate-100 rounded-full shadow-sm items-center ${activeTab === 'MCQ' ? QB_GRID_MCQ : QB_GRID_DESC}`}>
                      <div className="flex flex-col items-center justify-center gap-1">
                        {activeTab === 'DESCRIPTIVE' && (
                          <button
                            type="button"
                            onClick={() => setActiveTab('MCQ')}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-[#b91c1c] hover:bg-red-100 transition-all"
                            aria-label="Switch to MCQ questions"
                          >
                            <FiChevronLeft className="text-sm" />
                          </button>
                        )}
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">#</div>
                      </div>
                      <div className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest">PEDAGOGY</div>
                      <div className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest">QUESTION CONTENT</div>
                      {activeTab === 'MCQ' ? (
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest">OPTION DETAILS</div>
                          <button
                            type="button"
                            onClick={() => setActiveTab('DESCRIPTIVE')}
                            className="w-8 h-8 flex items-center justify-center bg-red-50 text-[#b91c1c] rounded-lg hover:bg-red-100 transition-all shrink-0"
                            aria-label="Switch to 2-mark questions"
                          >
                            <FiChevronRight className="text-sm" />
                          </button>
                        </div>
                      ) : (
                        <div aria-hidden="true" />
                      )}
                    </div>
                  </div>

                  {/* Question Input Row (Sticky below header) */}
                  {isAddingQuestion && (
                    <div className="assess-mod-add-question mb-6 shrink-0 w-full min-w-0">
                      <div className="bg-white border border-red-100 rounded-[24px] p-4 sm:p-6 shadow-xl shadow-red-500/5 animate-in fade-in slide-in-from-top-4 duration-300">
                        <div className="assess-mod-add-question-fields lg:overflow-x-auto lg:custom-scrollbar lg:pb-1">
                        <div
                          className={`assess-mod-add-question-grid flex flex-col gap-4 w-full min-w-0 lg:grid lg:gap-6 lg:items-start lg:min-w-[720px] ${
                            activeTab === 'MCQ'
                              ? 'lg:grid-cols-[60px_180px_minmax(0,1fr)_340px]'
                              : 'lg:grid-cols-[60px_180px_minmax(0,1fr)_72px]'
                          }`}
                        >
                          {/* Col 1: Index */}
                          <div className="flex flex-col items-center max-lg:flex-row max-lg:items-center max-lg:gap-3 max-lg:w-full max-lg:pb-3 max-lg:border-b max-lg:border-slate-100">
                            <div className="w-10 h-10 bg-[#1e293b] text-white rounded-full flex items-center justify-center font-black text-[14px] shadow-lg border-4 border-white shrink-0">
                              {questions.filter(q => (activeTab === 'MCQ' ? q.type === 'MCQ' : q.type === 'DESCRIPTIVE')).length + 1}
                            </div>
                            <div className={`max-lg:mt-0 mt-2 text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-widest ${activeTab === 'MCQ' ? 'bg-red-50 text-[#b91c1c] border-red-100' : 'bg-red-50 text-red-500 border-red-100'}`}>
                              {activeTab === 'MCQ' ? 'MCQ' : '2M'}
                            </div>
                          </div>

                        {/* Col 2: Pedagogy */}
                        <div className="relative" ref={pedagogyRef}>
                          <PedagogyFieldHeader
                            deleteMode={pedagogyDeleteMode}
                            onToggleDeleteMode={togglePedagogyDeleteMode}
                          />
                          <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                            <input 
                              type="text"
                              placeholder="Search or type pedagogy..."
                              className={`w-full bg-[#f8fafc] border rounded-lg px-3 pr-8 py-2 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 cursor-pointer ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-100'}`}
                              value={pedagogySearch}
                              onChange={(e) => {
                                setPedagogySearch(e.target.value);
                                if (!showPedagogyDropdown) {
                                  setShowPedagogyDropdown(true);
                                  requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                }
                              }}
                              onFocus={() => {
                                setShowPedagogyDropdown(true);
                                requestAnimationFrame(() => syncPedagogyDropdownPosition());
                              }}
                              onClick={togglePedagogyDropdown}
                            />
                            <FiChevronDown 
                              className={`absolute right-3 top-1/2 -translate-y-1/2 text-[12px] transition-all pointer-events-none ${showPedagogyDropdown ? 'text-[#b91c1c]' : 'text-slate-400'}`} 
                            />
                          </div>

                          {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                            <PedagogyDropdownMenu
                              menuRef={pedagogyMenuRef}
                              position={pedagogyDropdownPos}
                              filteredOptions={filteredPedagogyOptions}
                              {...pedagogyDropdownMenuProps}
                            />,
                            document.body
                          )}
                        </div>

                        {/* Col 3: Question Content */}
                        <div className="flex flex-col h-full">
                          <div className="mb-1.5">
                            <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5 max-lg:visible lg:invisible">QUESTION CONTENT</h6>
                          </div>
                          <textarea 
                            placeholder="Question..."
                            rows={activeTab === 'MCQ' ? "2" : "3"}
                            className={`w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none leading-relaxed ${activeTab === 'MCQ' ? 'min-h-[90px]' : 'min-h-[100px]'}`}
                            value={newQuestion}
                            onChange={(e) => setNewQuestion(e.target.value)}
                          />
                          {activeTab === 'DESCRIPTIVE' && (
                            <div className="mt-4">
                              <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-1.5">EXPECTED ANSWER</h6>
                              <textarea 
                                placeholder="Expected Answer..."
                                className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none min-h-[80px] leading-relaxed"
                                value={newCorrectAnswer}
                                onChange={(e) => setNewCorrectAnswer(e.target.value)}
                              />
                            </div>
                          )}
                        </div>

                        {activeTab === 'DESCRIPTIVE' && (
                          <div className="hidden lg:block" aria-hidden="true" />
                        )}

                        {/* Col 4: MCQ Options */}
                        {activeTab === 'MCQ' && (
                          <div className="w-full min-w-0">
                            <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-1.5 lg:hidden">OPTION DETAILS</h6>
                            <div className="grid grid-cols-2 gap-2">
                              {['A', 'B', 'C', 'D'].map(opt => (
                                <div key={opt} className="relative">
                                  <input 
                                    type="text"
                                    placeholder={`${opt} ...`}
                                    className={`w-full bg-white border rounded-lg pl-8 pr-8 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${correctOption === opt ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-100'}`}
                                    value={newOptions[opt]}
                                    onChange={(e) => setNewOptions({...newOptions, [opt]: e.target.value})}
                                  />
                                  <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-black transition-all ${correctOption === opt ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{opt}</span>
                                  <div 
                                    onClick={() => setCorrectOption(opt)}
                                    className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${correctOption === opt ? 'border-red-600 bg-[#b91c1c]' : 'border-slate-200 bg-white hover:border-red-400'}`}
                                  >
                                    {correctOption === opt && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        </div>
                        </div>
                        <div className="assess-mod-add-question-actions flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-5 pt-4 border-t border-slate-100 shrink-0">
                          <button
                            type="button"
                            onClick={() => setIsAddingQuestion(false)}
                            className="btn-assessment w-full sm:w-auto bg-white !text-slate-600 border border-slate-200 !shadow-none hover:!bg-slate-50 hover:!text-slate-800"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleManualSave}
                            className="btn-assessment btn-assessment-danger w-full sm:w-auto"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isAddingQuestion && questions.length === 0 && (
                    <div className="flex flex-1 flex-col items-center justify-center min-h-[240px] py-12 opacity-10">
                      <FiInbox className="text-6xl mb-4" />
                      <p className="text-[12px] font-black uppercase tracking-widest">No Questions Added</p>
                    </div>
                  )}

                  {/* Question List */}
                  <div className={`space-y-3 pb-0 min-w-0 ${isAddingQuestion ? 'mt-2' : ''}`}>
                    {questions.filter(q => (activeTab === 'MCQ' ? q.type === 'MCQ' : q.type === 'DESCRIPTIVE')).map((q, idx) => {
                      const isEditingRow = editingQuestionId === q.id;
                      const rowGridClass = q.type === 'MCQ' ? QB_GRID_MCQ : QB_GRID_DESC;

                      return (
                      <div
                        key={q.id}
                        onDoubleClick={() => !isEditingRow && setViewingQuestion(q)}
                        className={`rounded-[24px] p-5 shadow-sm transition-all group ${
                          isEditingRow
                            ? 'bg-white border border-red-100 shadow-xl shadow-red-500/5'
                            : 'bg-white border border-slate-100 hover:shadow-md cursor-pointer select-none'
                        }`}
                      >
                        {isEditingRow ? (
                          <>
                            <div className={`assess-mod-question-row grid gap-6 items-start ${rowGridClass}`}>
                              <div className="flex flex-col items-center pt-1">
                                <div className="w-8 h-8 bg-[#1e293b] text-white rounded-full flex items-center justify-center font-black text-[12px] shadow-sm">{idx + 1}</div>
                                <div className={`mt-1.5 text-[7px] font-black px-1.5 py-0.5 rounded border uppercase tracking-widest ${q.type === 'MCQ' ? 'bg-red-50 text-[#b91c1c] border-red-100' : 'bg-red-50 text-red-500 border-red-100'}`}>
                                  {q.type === 'MCQ' ? 'MCQ' : '2M'}
                                </div>
                              </div>

                              <div className="relative min-w-0" ref={pedagogyRef}>
                                <PedagogyFieldHeader
                                  deleteMode={pedagogyDeleteMode}
                                  onToggleDeleteMode={togglePedagogyDeleteMode}
                                  labelClassName="!text-slate-400 !text-[7px]"
                                />
                                <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                  <input
                                    type="text"
                                    placeholder="Search or type pedagogy..."
                                    className={`w-full bg-[#f8fafc] border rounded-lg px-3 pr-8 py-2 text-[11px] font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-100'}`}
                                    value={pedagogySearch}
                                    onChange={(e) => {
                                      setPedagogySearch(e.target.value);
                                      if (!showPedagogyDropdown) {
                                        setShowPedagogyDropdown(true);
                                        requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                      }
                                    }}
                                    onFocus={() => {
                                      setShowPedagogyDropdown(true);
                                      requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                    }}
                                    onClick={togglePedagogyDropdown}
                                  />
                                  <FiChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 text-[12px] pointer-events-none ${showPedagogyDropdown ? 'text-[#b91c1c]' : 'text-slate-400'}`} />
                                </div>
                                {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                  <PedagogyDropdownMenu
                                    menuRef={pedagogyMenuRef}
                                    position={pedagogyDropdownPos}
                                    filteredOptions={filteredPedagogyOptions}
                                    {...pedagogyDropdownMenuProps}
                                  />,
                                  document.body
                                )}
                              </div>

                              <div className="min-w-0">
                                <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1">QUESTION CONTENT</span>
                                <textarea
                                  placeholder="Question..."
                                  rows={q.type === 'MCQ' ? 2 : 3}
                                  className={`w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-[11px] font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none leading-relaxed ${q.type === 'MCQ' ? 'min-h-[72px]' : 'min-h-[80px]'}`}
                                  value={newQuestion}
                                  onChange={(e) => setNewQuestion(e.target.value)}
                                />
                                {q.type === 'DESCRIPTIVE' && (
                                  <div className="mt-3">
                                    <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1">EXPECTED ANSWER</span>
                                    <textarea
                                      placeholder="Expected Answer..."
                                      className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-[11px] font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none min-h-[64px] leading-relaxed"
                                      value={newCorrectAnswer}
                                      onChange={(e) => setNewCorrectAnswer(e.target.value)}
                                    />
                                  </div>
                                )}
                              </div>

                              {q.type === 'MCQ' && (
                                <div className="min-w-0">
                                  <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1">OPTION DETAILS</span>
                                  <div className="grid grid-cols-2 gap-2">
                                    {['A', 'B', 'C', 'D'].map(opt => (
                                      <div key={opt} className="relative">
                                        <input
                                          type="text"
                                          placeholder={`${opt} ...`}
                                          className={`w-full bg-white border rounded-lg pl-8 pr-8 py-2 text-[11px] font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${correctOption === opt ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-100'}`}
                                          value={newOptions[opt]}
                                          onChange={(e) => setNewOptions({ ...newOptions, [opt]: e.target.value })}
                                        />
                                        <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-black ${correctOption === opt ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{opt}</span>
                                        <div
                                          onClick={() => setCorrectOption(opt)}
                                          className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${correctOption === opt ? 'border-red-600 bg-[#b91c1c]' : 'border-slate-200 bg-white hover:border-red-400'}`}
                                        >
                                          {correctOption === opt && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-4 pt-4 border-t border-slate-100">
                              <button
                                type="button"
                                onClick={handleCancelInlineEdit}
                                className="btn-assessment w-full sm:w-auto bg-white !text-slate-600 border border-slate-200 !shadow-none hover:!bg-slate-50 hover:!text-slate-800"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleManualSave}
                                className="btn-assessment btn-assessment-danger w-full sm:w-auto"
                              >
                                Save
                              </button>
                            </div>
                          </>
                        ) : (
                        <>
                          {/* Status bar — full width across the card top */}
                          <div className="flex items-center justify-between mb-3 pb-2.5 border-b border-slate-100" onDoubleClick={(e) => e.stopPropagation()}>
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">QUESTION STATUS</span>
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] font-black uppercase tracking-widest ${ (q.status || 'Active') === 'Active' ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {(q.status || 'Active') === 'Active' ? 'Active' : 'Inactive'}
                              </span>
                              {/* Toggle switch */}
                              <button
                                type="button"
                                title={(q.status || 'Active') === 'Active' ? 'Deactivate question' : 'Activate question'}
                                onClick={() => handleToggleStatus(q)}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${ (q.status || 'Active') === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`}
                              >
                                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${ (q.status || 'Active') === 'Active' ? 'translate-x-4' : 'translate-x-0'}`} />
                              </button>
                            </div>
                          </div>
                          <div className={`assess-mod-question-row grid gap-6 items-start ${rowGridClass} ${ (q.status || 'Active') === 'Inactive' ? 'opacity-40' : ''}`}>
                            <div className="flex flex-col items-center">
                              <div className="w-8 h-8 bg-white border border-slate-200 text-[#1e293b] rounded-full flex items-center justify-center font-black text-[12px] shadow-sm">{idx + 1}</div>
                              <div className={`mt-1.5 text-[7px] font-black px-1.5 py-0.5 rounded border uppercase tracking-widest ${q.type === 'MCQ' ? 'bg-red-50 text-[#b91c1c] border-red-100' : 'bg-red-50 text-red-500 border-red-100'}`}>
                                {q.type === 'MCQ' ? 'MCQ' : '2M'}
                              </div>
                            </div>

                            <div className="min-w-0 self-center">
                              <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1 lg:hidden">PEDAGOGY</span>
                              <h5 className="text-[11px] font-black text-[#1e293b] leading-tight truncate">{q.pedagogy || 'General Knowledge'}</h5>
                            </div>

                            <div className="min-w-0 self-center">
                              <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1 lg:hidden">QUESTION CONTENT</span>
                              <h5 className="text-[11px] font-bold text-slate-600 leading-relaxed line-clamp-2">
                                {q.question.replace(/\s*\(Q\d+\)$/i, '')}
                              </h5>
                              {q.type === 'DESCRIPTIVE' && (
                                <p className="mt-1.5 text-[10px] italic text-slate-400 line-clamp-1">
                                  Expected: {q.correct_answer}
                                </p>
                              )}
                            </div>

                            {isMcqQuestionType(q.type) ? (
                              <div className="flex items-center gap-4 justify-end min-w-0 self-center">
                                <div className="grid grid-cols-2 gap-1.5 flex-1 min-w-0">
                                  {MCQ_OPTION_LETTERS.map((letter) => (
                                    <div
                                      key={letter}
                                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[9px] font-bold ${
                                        isMcqCorrectOption(q, letter)
                                          ? 'assess-mod-mcq-option--correct'
                                          : 'bg-slate-50 border-slate-100 text-slate-500'
                                      }`}
                                    >
                                      <span className={`assess-mod-mcq-option-letter w-4 h-4 rounded flex items-center justify-center text-[8px] ${ isMcqCorrectOption(q, letter) ? 'assess-mod-mcq-option-letter--correct' : 'bg-white border border-slate-200'}`}>
                                        {letter}
                                      </span>
                                      <span className="truncate">{getMcqOptionText(q, letter)}</span>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex flex-col items-center gap-3 shrink-0" onDoubleClick={(e) => e.stopPropagation()}>
                                  <AssessmentEditIconButton
                                    onClick={(q.status || 'Active') === 'Active' ? () => handleEditQuestion(q) : undefined}
                                    title={(q.status || 'Active') === 'Active' ? 'Edit question' : 'Activate question to edit'}
                                    className={(q.status || 'Active') === 'Inactive' ? '!opacity-30 !cursor-not-allowed pointer-events-none' : ''}
                                  />
                                  <AssessmentDeleteIconButton
                                    onClick={(q.status || 'Active') === 'Active' ? () => handleDeleteQuestion(q.id) : undefined}
                                    title={(q.status || 'Active') === 'Active' ? 'Delete question' : 'Activate question to delete'}
                                    className={(q.status || 'Active') === 'Inactive' ? '!opacity-30 !cursor-not-allowed pointer-events-none' : ''}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center gap-3 shrink-0 self-center" onDoubleClick={(e) => e.stopPropagation()}>
                                <AssessmentEditIconButton
                                  onClick={(q.status || 'Active') === 'Active' ? () => handleEditQuestion(q) : undefined}
                                  title={(q.status || 'Active') === 'Active' ? 'Edit question' : 'Activate question to edit'}
                                  className={(q.status || 'Active') === 'Inactive' ? '!opacity-30 !cursor-not-allowed pointer-events-none' : ''}
                                />
                                <AssessmentDeleteIconButton
                                  onClick={(q.status || 'Active') === 'Active' ? () => handleDeleteQuestion(q.id) : undefined}
                                  title={(q.status || 'Active') === 'Active' ? 'Delete question' : 'Activate question to delete'}
                                  className={(q.status || 'Active') === 'Inactive' ? '!opacity-30 !cursor-not-allowed pointer-events-none' : ''}
                                />
                              </div>
                            )}
                          </div>
                        </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
          </div>
          {!isWizardMode && (
            <div className="assess-mod-qb-save-fixed flex items-center justify-end">
              <button
                type="button"
                onClick={handleSave}
                className="btn-assessment btn-assessment-danger"
              >
                Save Question Bank
              </button>
            </div>
          )}
        </main>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
        .animate-dash {
          animation: dash 2s ease-in-out forwards;
        }
      `}</style>
      <AssessmentPopup 
        {...popupConfig}
        inputValue={popupInput}
        onInputChange={setPopupInput}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
      {viewingQuestion && (
        <AssessmentPopup
          isOpen={true}
          title="Question Details"
          message=""
          type="info"
          showCancel={false}
          confirmText="Close"
          containerClassName="!max-w-2xl w-full"
          onConfirm={() => setViewingQuestion(null)}
          onCancel={() => setViewingQuestion(null)}
        >
          <div className="text-left mt-2 space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar w-full">
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.type}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.pedagogy || 'General'}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.marks} Marks
              </span>
            </div>

            <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Question</h4>
              <p className="text-sm font-bold text-[#1e293b] whitespace-pre-wrap leading-relaxed">
                {viewingQuestion.question.replace(/\s*\(Q\d+\)$/i, '')}
              </p>
            </div>

            {isMcqQuestionType(viewingQuestion.type) ? (
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Options</h4>
                <div className="grid grid-cols-1 gap-2">
                  {['A', 'B', 'C', 'D'].map((letter) => {
                    const isCorrect = isMcqCorrectOption(viewingQuestion, letter);
                    return (
                      <div
                        key={letter}
                        className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-xs font-bold leading-relaxed ${
                          isCorrect
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'bg-slate-50 border-slate-100 text-slate-600'
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-black shrink-0 ${
                            isCorrect ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200'
                          }`}
                        >
                          {letter}
                        </span>
                        <span className="break-words text-left">{getMcqOptionText(viewingQuestion, letter)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              (viewingQuestion.correct_answer || viewingQuestion.expected_answer) && (
                <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Expected Answer</h4>
                  <p className="text-sm font-bold text-slate-600 whitespace-pre-wrap leading-relaxed text-left">
                    {viewingQuestion.correct_answer || viewingQuestion.expected_answer}
                  </p>
                </div>
              )
            )}
          </div>
        </AssessmentPopup>
      )}
    </div>
  );
};

export default QuestionBankManager;
